const apiUrl = process.env.REACT_APP_API_URL;

function getBasicAuthHeader() {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  return `Basic ${btoa(`${username}:${password}`)}`;
}

function currentAdminUserId() {
  // The super admin's real id (root id when impersonating).
  return localStorage.getItem('userId1') || localStorage.getItem('userId');
}

// Exported so other System admin screens (e.g. PublishingManager) can reuse the
// same Basic-auth fetch conventions instead of re-declaring them.
export function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Accept: '*/*',
    Authorization: getBasicAuthHeader(),
  };
}

export async function parseError(response, fallback) {
  let message = fallback || `Request failed: ${response.status}`;
  try {
    const body = await response.json();
    if (body?.message) message = body.message;
  } catch (_) {
    /* ignore */
  }
  return new Error(message);
}

/** Lists all campaigns (newest first) with their attached-org counts. */
export async function fetchCampaigns() {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/campaigns?adminUserId=${encodeURIComponent(adminUserId)}`,
    { method: 'GET', headers: authHeaders() },
  );
  if (!response.ok) {
    throw await parseError(response, `Campaigns request failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Creates a campaign. `payload` carries the form fields (name, description, type,
 * showDemoVideo, demoVideoUrl/Title/Subtitle, isLeadFormEnabled, active, startDate, endDate).
 */
export async function createCampaign(payload) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(`${apiUrl}/api/admin/campaigns`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ adminUserId, ...payload }),
  });
  if (!response.ok) {
    throw await parseError(response, 'Create failed');
  }
  return response.json();
}

/** Updates an existing campaign identified by its campaignId. */
export async function updateCampaign(campaignId, payload) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/campaigns/${encodeURIComponent(campaignId)}`,
    {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ adminUserId, ...payload }),
    },
  );
  if (!response.ok) {
    throw await parseError(response, 'Update failed');
  }
  return response.json();
}

/** Toggles a campaign's active flag. */
export async function setCampaignActive(campaignId, active) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/campaigns/${encodeURIComponent(campaignId)}/active`,
    {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ adminUserId, active: !!active }),
    },
  );
  if (!response.ok) {
    throw await parseError(response, 'Update failed');
  }
  return response.json();
}

/** Loads the org picker payload: { orgs: [{ orgId, orgName, attached }], attachedOrgIds }. */
export async function fetchCampaignOrgs(campaignId) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/campaigns/${encodeURIComponent(campaignId)}/orgs?adminUserId=${encodeURIComponent(adminUserId)}`,
    { method: 'GET', headers: authHeaders() },
  );
  if (!response.ok) {
    throw await parseError(response, `Org list failed: ${response.status}`);
  }
  return response.json();
}

/** Sets the exact set of orgs attached to the campaign. */
export async function assignCampaignOrgs(campaignId, orgIds) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/campaigns/${encodeURIComponent(campaignId)}/orgs`,
    {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ adminUserId, orgIds: orgIds || [] }),
    },
  );
  if (!response.ok) {
    throw await parseError(response, 'Assign failed');
  }
  return response.json();
}
